import { apiGet, apiPost, apiPatch, apiDelete, apiDeleteWithBody } from "../../shared/http.js";
import {
  CreateRecipeRequestSchema,
  RecipeApiItemSchema,
  RecipeListResponseSchema,
  type CreateRecipeRequest,
  type PatchRecipeRequest,
  type RecipeApiItem,
  type RecipeListResponse,
  type PresignPhotoResponse,
  type ConfirmPhotoRequest,
  type ConfirmPhotoResponse,
  type DeletePhotoResponse,
  type CdnCookiesResponse,
} from "@recipevault/shared";
import { z } from "zod";

interface ListOptions {
  updatedSince?: string;
  cursor?: string;
  limit?: number;
}

export async function listRecipes(opts: ListOptions = {}): Promise<RecipeListResponse> {
  const qs = new URLSearchParams();
  if (opts.updatedSince) qs.set("updatedSince", opts.updatedSince);
  if (opts.cursor) qs.set("cursor", opts.cursor);
  if (opts.limit !== undefined) qs.set("limit", String(opts.limit));
  const path = qs.toString() ? `/recipes?${qs.toString()}` : "/recipes";
  const raw = await apiGet<unknown>(path);
  return RecipeListResponseSchema.parse(raw);
}

const CreateRecipeResponseSchema = z.object({ recipe: RecipeApiItemSchema });
type CreateRecipeResponse = z.infer<typeof CreateRecipeResponseSchema>;

export async function createRecipe(
  body: CreateRecipeRequest,
  idempotencyKey: string,
): Promise<CreateRecipeResponse> {
  CreateRecipeRequestSchema.parse(body);
  const raw = await apiPost<unknown>("/recipes", body, { idempotencyKey });
  return CreateRecipeResponseSchema.parse(raw);
}

const GetRecipeResponseSchema = z.object({ recipe: RecipeApiItemSchema });
type GetRecipeResponse = z.infer<typeof GetRecipeResponseSchema>;

export async function getRecipe(id: string): Promise<GetRecipeResponse> {
  const raw = await apiGet<unknown>(`/recipes/${id}`);
  return GetRecipeResponseSchema.parse(raw);
}

const PatchRecipeResponseSchema = z.object({ recipe: RecipeApiItemSchema });
type PatchRecipeResponse = z.infer<typeof PatchRecipeResponseSchema>;

// On 409, the server returns { error: {code:'VERSION_CONFLICT'}, current: <recipe> }.
// `friendlyError` and the shared `ApiError` flow surface the code; callers handle
// `current` by reading ApiError.responseBody — see SyncService for the pattern.
export async function patchRecipe(
  id: string,
  body: PatchRecipeRequest,
): Promise<PatchRecipeResponse> {
  const raw = await apiPatch<unknown>(`/recipes/${id}`, body);
  return PatchRecipeResponseSchema.parse(raw);
}

export async function deleteRecipe(id: string): Promise<void> {
  await apiDelete(`/recipes/${id}`);
}

export async function presignPhoto(recipeId: string): Promise<PresignPhotoResponse> {
  return apiPost<PresignPhotoResponse>(`/recipes/${recipeId}/photos/presign`, {});
}

export async function confirmPhoto(
  recipeId: string,
  body: ConfirmPhotoRequest,
  idempotencyKey: string,
): Promise<ConfirmPhotoResponse> {
  return apiPost<ConfirmPhotoResponse>(`/recipes/${recipeId}/photos/confirm`, body, {
    idempotencyKey,
  });
}

export async function deletePhoto(
  recipeId: string,
  photoKey: string,
  version: number,
): Promise<DeletePhotoResponse> {
  return apiDeleteWithBody<DeletePhotoResponse>(`/recipes/${recipeId}/photos/${photoKey}`, {
    version,
  });
}

export async function postCdnCookies(): Promise<CdnCookiesResponse> {
  // credentials: "include" is required so the browser persists the Set-Cookie
  // headers from the photos.recipevault.ca CDN domain (cross-origin HttpOnly cookies).
  return apiPost<CdnCookiesResponse>("/me/cdn-cookies", {}, { credentials: "include" });
}

export async function clearCdnCookies(): Promise<{ ok: true }> {
  // DELETE /me/cdn-cookies — server issues Set-Cookie ... Max-Age=0 for the three
  // CloudFront-* cookies. credentials: "include" is required so the browser
  // applies the clearing Set-Cookie headers for the cross-origin photos domain.
  // The x-household-id header is auto-injected by the http.ts request() helper
  // from the module-level activeHouseholdId state.
  return apiDelete<{ ok: true }>("/me/cdn-cookies", { credentials: "include" });
}

export type { RecipeApiItem };
